import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './EnterpriseQuote.css';

interface AcademicQuoteFormData {
  institutionName: string;
  institutionType: string;
  department: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  position: string;
  expectedUsers: string;
  studentCount: string;
  useCase: string;
  currentSolution: string;
  budget: string;
  timeline: string;
  requirements: string[];
  message: string;
}

const AcademicQuote: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<AcademicQuoteFormData>({
    institutionName: '',
    institutionType: '',
    department: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    position: '',
    expectedUsers: '',
    studentCount: '',
    useCase: '',
    currentSolution: '',
    budget: '',
    timeline: '',
    requirements: [],
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const requirementOptions = [
    t('quote.academic.requirements.lmsIntegration'),
    t('quote.academic.requirements.gradeManagement'),
    t('quote.academic.requirements.studentPortal'),
    t('quote.academic.requirements.parentSharing'),
    t('quote.academic.requirements.courseTemplates'),
    t('quote.academic.requirements.learningAnalytics'),
    t('quote.academic.requirements.multilingualEng'),
    t('quote.academic.requirements.teacherTraining'),
    t('quote.academic.requirements.implementationSupport'),
    t('quote.corporate.requirements.dataMigration'),
    t('common.other')
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequirementChange = (requirement: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.includes(requirement)
        ? prev.requirements.filter(req => req !== requirement)
        : [...prev.requirements, requirement]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/enterprise-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'academic',
          ...formData
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        throw new Error(t('quote.error.submitFailed'));
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert(t('quote.error.submitFailedRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="enterprise-quote academic">
        <div className="container">
          <div className="quote-success">
            <div className="success-icon academic">🎓</div>
            <h1>{t('quote.success.title')}</h1>
            <p>
              {t('quote.success.academic.message')}
            </p>
            <div className="success-actions">
              <button 
                onClick={() => window.close()}
                className="button-primary academic"
              >
                {t('common.close')}
              </button>
              <a href="/" className="button-secondary">
                {t('common.backToDashboard')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-quote academic">
      <div className="container">
        <div className="quote-header">
          <h1>{t('quote.title')}</h1>
          <p className="quote-subtitle academic">{t('quote.academic.subtitle')}</p>
          <p className="quote-description">
            {t('quote.academic.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-section">
            <h2>{t('quote.academic.sections.institutionInfo')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="institutionName">{t('quote.academic.fields.institutionName')} *</label>
                <input
                  type="text"
                  id="institutionName"
                  name="institutionName"
                  value={formData.institutionName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="institutionType">{t('quote.academic.fields.institutionType')} *</label>
                <select
                  id="institutionType"
                  name="institutionType"
                  value={formData.institutionType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="university">{t('quote.academic.institutionTypes.university')}</option>
                  <option value="college">{t('quote.academic.institutionTypes.college')}</option>
                  <option value="high-school">{t('quote.academic.institutionTypes.highSchool')}</option>
                  <option value="junior-high">{t('quote.academic.institutionTypes.juniorHigh')}</option>
                  <option value="elementary">{t('quote.academic.institutionTypes.elementary')}</option>
                  <option value="vocational">{t('quote.academic.institutionTypes.vocational')}</option>
                  <option value="other">{t('common.other')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="department">{t('quote.academic.fields.department')}</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.contactInfo')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="contactName">{t('quote.fields.name')} *</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="position">{t('quote.academic.fields.positionTitle')}</label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder={t('quote.academic.placeholders.position')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactEmail">{t('userSettings.email')} *</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactPhone">{t('quote.fields.phone')}</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.usagePlan')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="expectedUsers">{t('quote.academic.fields.expectedUsers')} *</label>
                <select
                  id="expectedUsers"
                  name="expectedUsers"
                  value={formData.expectedUsers}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="5-20">{t('quote.academic.userCounts.small')}</option>
                  <option value="21-50">{t('quote.academic.userCounts.medium')}</option>
                  <option value="51-100">{t('quote.academic.userCounts.large')}</option>
                  <option value="101-300">{t('quote.academic.userCounts.xlarge')}</option>
                  <option value="301-500">{t('quote.academic.userCounts.xxlarge')}</option>
                  <option value="500+">{t('quote.academic.userCounts.enterprise')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="studentCount">{t('quote.academic.fields.studentCount')}</label>
                <select
                  id="studentCount"
                  name="studentCount"
                  value={formData.studentCount}
                  onChange={handleInputChange}
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="under-100">{t('quote.academic.studentCounts.under100')}</option>
                  <option value="100-500">{t('quote.academic.studentCounts.small')}</option>
                  <option value="501-1000">{t('quote.academic.studentCounts.medium')}</option>
                  <option value="1001-3000">{t('quote.academic.studentCounts.large')}</option>
                  <option value="3001-10000">{t('quote.academic.studentCounts.xlarge')}</option>
                  <option value="over-10000">{t('quote.academic.studentCounts.xxlarge')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="timeline">{t('quote.fields.timeline')} *</label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="immediate">{t('quote.timeline.immediate')}</option>
                  <option value="next-semester">{t('quote.academic.timeline.nextSemester')}</option>
                  <option value="next-year">{t('quote.academic.timeline.nextYear')}</option>
                  <option value="6months">{t('quote.timeline.sixMonths')}</option>
                  <option value="1year">{t('quote.timeline.oneYear')}</option>
                  <option value="undecided">{t('quote.timeline.undecided')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="useCase">{t('quote.fields.useCase')} *</label>
              <textarea
                id="useCase"
                name="useCase"
                value={formData.useCase}
                onChange={handleInputChange}
                placeholder={t('quote.academic.placeholders.useCase')}
                rows={3}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentSolution">{t('quote.academic.fields.currentSolution')}</label>
              <textarea
                id="currentSolution"
                name="currentSolution"
                value={formData.currentSolution}
                onChange={handleInputChange}
                placeholder={t('quote.academic.placeholders.currentSolution')}
                rows={2}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.budgetRequirements')}</h2>
            <div className="form-group">
              <label htmlFor="budget">{t('quote.academic.fields.budgetAnnual')}</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">{t('common.pleaseSelect')}</option>
                <option value="under-1m">{t('quote.academic.budget.under1m')}</option>
                <option value="1m-3m">{t('quote.academic.budget.1m3m')}</option>
                <option value="3m-5m">{t('quote.academic.budget.3m5m')}</option>
                <option value="5m-10m">{t('quote.academic.budget.5m10m')}</option>
                <option value="over-10m">{t('quote.academic.budget.over10m')}</option>
                <option value="undecided">{t('quote.timeline.undecided')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('quote.fields.requirements')}</label>
              <div className="checkbox-grid">
                {requirementOptions.map((requirement) => (
                  <label key={requirement} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.requirements.includes(requirement)}
                      onChange={() => handleRequirementChange(requirement)}
                    />
                    <span>{requirement}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="message">{t('quote.fields.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t('quote.academic.placeholders.message')}
                rows={4}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="button-primary academic submit-button"
            >
              {isSubmitting ? t('common.submitting') : t('quote.submit')}
            </button>
            <p className="form-note">
              {t('quote.academic.formNote')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcademicQuote;